import removeSelectedFilter from '@/catalog/utils/remove-selected-filter';
import { useCallback } from 'react';
import { FilterCategory, IndexedNestedFilter, IndexedFilters } from '../types/filters';

export interface NestedCheckboxTogglesProps {
  /** Name of the filter category, e.g. "makes". */
  categoryName: FilterCategory;
  /** Available filters within this category. */
  categoryFilters: IndexedNestedFilter;
  /** A setState function to update selectedFilters. */
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
}

export interface NestedCheckboxTogglesResponse {
  /** Function to toggle the checked state of a main filter's checkbox. */
  toggleMainCheckbox: (checked: boolean, mainValue: string) => void;
  /** Function to toggle the checked state of a sub-filter's checkbox. */
  toggleSubCheckbox: (checked: boolean, mainValue: string, subValue: string) => void;
}

/**
 * Custom hook that provides toggle handlers for nested checkbox groups.
 * The hook is responsible for adding or removing selected filters (both main and sub)
 * based on user interactions with checkboxes.
 *
 * Note: We do `prev[categoryName] as IndexedNestedFilter`, but we
 * can assume that the category value is an IndexedNestedFilter because
 * of the Type Narrowing that happens in FilterMenuCategory.
 */
export const useNestedCheckboxToggles = ({
  categoryName,
  categoryFilters,
  setSelectedFilters,
}: NestedCheckboxTogglesProps) => {
  /**
   * Add a main filter along with all its sub-values.
   * This is triggered when a main filter checkbox is selected.
   */
  const addMainFilter = useCallback(
    (mainValue: string) => {
      setSelectedFilters((prev) => {
        // If the main filter doesn't have sub-values, don't make any changes.
        if (!categoryFilters[mainValue]) return prev;

        // Add the main filter only, not any of its sub-values to the category. This is equivalent to filtering for all vehicles by the given make.
        // E.g. { makes: { 'Audi': [] }
        const updatedCategory = {
          ...prev[categoryName],
          [mainValue]: [],
        };

        // Update the category.
        return { ...prev, [categoryName]: updatedCategory };
      });
    },
    [setSelectedFilters, categoryFilters, categoryName]
  );

  /**
   * Remove a main filter and all its associated sub-values.
   * This is triggered when a main filter checkbox is deselected.
   */
  const removeMainFilter = useCallback(
    (mainValue: string) => {
      setSelectedFilters((prev) =>
        removeSelectedFilter({ key: categoryName, value: mainValue }, prev)
      );
    },
    [categoryName, setSelectedFilters]
  );

  /**
   * Add a specific sub-filter under its associated main filter.
   * Triggered when a sub-filter checkbox is selected.
   */
  const addSubFilter = useCallback(
    (mainValue: string, subValue: string) => {
      setSelectedFilters((prev) => {
        if (
          // If this category does not exist
          !prev[categoryName] ||
          // Or the main filter does not exist in the category
          !(prev[categoryName] as IndexedNestedFilter)[mainValue] ||
          // Or the sub-filter is already in the main filter
          (prev[categoryName] as IndexedNestedFilter)[mainValue].includes(subValue)
        )
          // Don't make any changes.
          return prev;

        return {
          ...prev,
          [categoryName]: {
            ...prev[categoryName],
            // Append the sub-filter to the list of selected sub-filters.
            [mainValue]: [
              ...(prev[categoryName] as IndexedNestedFilter)[mainValue],
              subValue,
            ],
          },
        };
      });
    },
    [categoryName, setSelectedFilters]
  );

  /**
   * Remove a specific sub-filter from its associated main filter.
   * Triggered when a sub-filter checkbox is deselected.
   */
  const removeSubFilter = useCallback(
    (mainValue: string, subValue: string) => {
      setSelectedFilters((prev) =>
        removeSelectedFilter({ key: categoryName, value: subValue }, prev, false, true)
      );
    },
    [categoryName, setSelectedFilters]
  );

  /**
   * Handle toggle actions for main filter checkboxes.
   */
  const toggleMainCheckbox = useCallback(
    (checked: boolean, mainValue: string) => {
      if (checked) {
        removeMainFilter(mainValue);
      } else {
        addMainFilter(mainValue);
      }
    },
    [addMainFilter, removeMainFilter]
  );

  /**
   * Handle toggle actions for sub-filter checkboxes.
   */
  const toggleSubCheckbox = useCallback(
    (checked: boolean, mainValue: string, subValue: string) => {
      if (checked) {
        removeSubFilter(mainValue, subValue);
      } else {
        addSubFilter(mainValue, subValue);
      }
    },
    [addSubFilter, removeSubFilter]
  );

  return {
    toggleMainCheckbox,
    toggleSubCheckbox,
  };
};
